// src/components/dfs/ProjectionsTab.tsx
// Projections tab — total projected points + per-stat projected counts with opponent defense rank, virtualized.
// Columns: Name | PROJ | GL | SH | SoT | SC | GC | IP | IC | DIS | Add

import React, { useEffect, useState, useMemo, useRef, ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useVirtualizer } from '@tanstack/react-virtual';
import { ApiPlayer, PlayerProjectionDetail, fetchAllCompetitions } from '@/api/dfs';
import { contestProjectionsQueryOptions } from '@/hooks/useDfsResearchQueries';
import { formatPlayerDisplayName, foldForSearch } from '@/utils/formatting';
import { leagueSizes } from '@/utils/teams';
import HorizontalPillFilter from '@/components/common/HorizontalPillFilter';
import PlayerProfileModal from '@/components/common/PlayerProfileModal';
import Spinner from '@/components/common/Spinner';
import AvailabilityBadge from '@/components/common/AvailabilityBadge';
import InjuryBadge from '@/components/common/InjuryBadge';
import { Lock, CircleCheck } from 'lucide-react';
import { userFacingError } from '@/utils/userFacingError';

type PositionFilter = 'ALL' | 'FW' | 'MF' | 'DF' | 'GK';

// Stat keys as returned by the backend
type StatKey = 'Goal' | 'Shot' | 'ShotOnTarget' | 'SCA' | 'GCA' | 'ProgPass' | 'ProgCarry' | 'Dispossessed';

type SortCol = 'name' | 'projected' | StatKey;
type SortDir = 'asc' | 'desc';

// Point values per projection stat key (mirrors backend projection_service)
const STAT_POINT_VALUES: Record<string, number> = {
    Goal: 8, Shot: 1, ShotOnTarget: 3, SCA: 1.5, GCA: 6,
    ProgPass: 1, ProgCarry: 1, Dispossessed: -1,
};

const calcTotalProjected = (proj: PlayerProjectionDetail | undefined): number | null => {
    if (!proj?.projected_stats) return null;
    let total = 0;
    for (const [key, pts] of Object.entries(STAT_POINT_VALUES)) {
        const val = proj.projected_stats[key as keyof typeof proj.projected_stats];
        if (val != null) total += val * pts;
    }
    return total;
};

interface ProjectionsTabProps {
    contestId: number;
    competitionId: number;
    rankCompetitionIds?: number[];
    token: string | null;
    availablePlayers: ApiPlayer[];
    onAddPlayer: (player: ApiPlayer) => void;
    lineupPlayerIds?: Set<number>;
    lockedPlayerIds: Set<number>;
    remainingSalary?: number;
    positionFilter: PositionFilter;
    onPositionFilterChange: (f: PositionFilter) => void;
    searchTerm: string;
    onSearchTermChange: (t: string) => void;
    leagueShortNameForOprkContext?: string | null;
    sortConfig: { key: string; direction: 'asc' | 'desc' };
    onSortConfigChange: (config: { key: string; direction: 'asc' | 'desc' }) => void;
    isPoolLoading?: boolean;
    hideSearch?: boolean;
}

const fmtProj = (v: number | undefined) =>
    (v ?? 0).toFixed(1);

const fmtSalary = (v: number | null | undefined) =>
    v == null ? '' : `$${(v / 1000).toFixed(1)}K`;

// Rank coloring: rank 1 = toughest defense = red, high rank = soft defense = green
const getRankColor = (rank: number | undefined, leagueSize: number) => {
    if (rank == null) return 'text-gray-400';
    const pct = rank / leagueSize;
    if (pct <= 0.33) return 'text-secondary';   // toughest 1/3 — red
    if (pct <= 0.67) return 'text-yellow-400';  // middle
    return 'text-green-400';                     // softest 1/3 — green
};

// Name | PROJ | GL | SH | SoT | SC | GC | IP | IC | DIS | Add
const GRID = 'grid-cols-[minmax(0,2fr)_72px_52px_52px_56px_52px_52px_52px_52px_56px_76px]';

function SortBtn({
    label, colKey, sortCol, sortDir, onClick, align = 'right',
}: {
    label: string; colKey: SortCol; sortCol: SortCol; sortDir: SortDir;
    onClick: (c: SortCol) => void; align?: 'left' | 'right';
}) {
    const active = sortCol === colKey;
    const arrow = active ? (sortDir === 'asc' ? '↑' : '↓') : '↕';
    const justify = align === 'left' ? 'justify-start' : 'justify-end';
    return (
        <button
            type="button"
            className={`flex items-center gap-0.5 ${justify} hover:text-white active:text-white transition-colors w-full ${active ? 'text-accent-yellow' : ''}`}
            onClick={() => onClick(colKey)}
        >
            {align !== 'right' && <span>{label}</span>}
            <span className="text-micro opacity-50">{arrow}</span>
            {align === 'right' && <span>{label}</span>}
        </button>
    );
}

const ProjectionsTab: React.FC<ProjectionsTabProps> = ({
    contestId,
    rankCompetitionIds,
    competitionId,
    token,
    availablePlayers,
    onAddPlayer,
    lineupPlayerIds = new Set(),
    lockedPlayerIds,
    remainingSalary,
    positionFilter,
    onPositionFilterChange,
    searchTerm,
    onSearchTermChange,
    leagueShortNameForOprkContext,
    sortConfig,
    onSortConfigChange,
    isPoolLoading,
    hideSearch,
}) => {
    // Cached projections query (shared definition with the page-level
    // prefetch) - replaces the per-mount effect fetch that rebuilt this data
    // from scratch on every visit.
    const {
        data: projections = [] as PlayerProjectionDetail[],
        isPending: isLoadingProjections,
        error: projQueryError,
    } = useQuery(contestProjectionsQueryOptions(contestId, token));
    const loadError = projQueryError ? userFacingError(projQueryError, 'Failed to load projections.') : null;
    const scrollRef = useRef<HTMLDivElement>(null);

    // Derive sort from parent config
    const sortCol = (sortConfig.key || 'projected') as SortCol;
    const sortDir = (sortConfig.direction || 'desc') as SortDir;

    const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
    const [selectedPlayerForModal, setSelectedPlayerForModal] = useState<ApiPlayer | null>(null);
    const [modalCompInfo, setModalCompInfo] = useState<{ id: number; name: string } | null>(null);

    // Modal competition context follows the contest's actual competition (EPL or MLS).
    // Falls back to fetching the competition list only if the parent didn't pass an id.
    useEffect(() => {
        if (competitionId) {
            setModalCompInfo({ id: competitionId, name: leagueShortNameForOprkContext || '' });
            return;
        }
        fetchAllCompetitions(true)
            .then(comps => {
                const fallback = comps[0];
                if (fallback) setModalCompInfo({ id: fallback.id, name: fallback.name });
            })
            .catch(() => {});
    }, [competitionId, leagueShortNameForOprkContext]);

    const handlePlayerRowClick = (player: ApiPlayer) => {
        setSelectedPlayerForModal(player);
        setIsProfileModalOpen(true);
    };

    // League size drives rank-coloring thresholds (toughest 1/3 = red, etc.).
    // EPL has 20 teams, MLS has 30 — picking the wrong one mis-colors OPRK pills.
    const leagueSize =
        (leagueShortNameForOprkContext && leagueSizes[leagueShortNameForOprkContext]) || 20;

    // Projection data keyed by player_id
    const projMap = useMemo(() => {
        const m = new Map<number, PlayerProjectionDetail>();
        projections.forEach(p => m.set(p.player_id, p));
        return m;
    }, [projections]);

    useEffect(() => {
        if (scrollRef.current) scrollRef.current.scrollTop = 0;
    }, [sortCol, sortDir]);

    const handleSort = (col: SortCol) => {
        if (col === sortCol) {
            onSortConfigChange({ key: col, direction: sortDir === 'asc' ? 'desc' : 'asc' });
        } else {
            onSortConfigChange({ key: col, direction: col === 'name' ? 'asc' : 'desc' });
        }
    };

    // Client-side filter + sort over the full pool
    const rows = useMemo(() => {
        return availablePlayers
            .filter(dfs => {
                if (positionFilter !== 'ALL' && dfs.primary_position !== positionFilter) return false;
                if (searchTerm) {
                    const name = foldForSearch(formatPlayerDisplayName(dfs));
                    if (!name.includes(foldForSearch(searchTerm))) return false;
                }
                return true;
            })
            .map(dfs => ({ dfs, proj: projMap.get(dfs.id) }))
            .sort((a, b) => {
                const dir = sortDir === 'asc' ? 1 : -1;
                if (sortCol === 'name') {
                    return dir * formatPlayerDisplayName(a.dfs).localeCompare(formatPlayerDisplayName(b.dfs));
                }
                if (sortCol === 'projected') {
                    const aVal = calcTotalProjected(a.proj) ?? -999;
                    const bVal = calcTotalProjected(b.proj) ?? -999;
                    return dir * (aVal - bVal);
                }
                const statKey = sortCol as StatKey;
                const aVal = a.proj?.projected_stats?.[statKey] ?? -1;
                const bVal = b.proj?.projected_stats?.[statKey] ?? -1;
                return dir * (aVal - bVal);
            });
    }, [availablePlayers, projMap, sortCol, sortDir, positionFilter, searchTerm]);

    // Virtualization
    const rowVirtualizer = useVirtualizer({
        count: rows.length,
        getScrollElement: () => scrollRef.current,
        estimateSize: () => 40,
        overscan: 6,
    });

    const filterItems = [
        { id: 'ALL' as const, name: 'All' },
        { id: 'FW' as const, name: 'FW' },
        { id: 'MF' as const, name: 'MF' },
        { id: 'DF' as const, name: 'DF' },
        { id: 'GK' as const, name: 'GK' },
    ];

    // Helper: render a single stat cell — projected pts (white) + OPRK rank (color-coded)
    const StatCell = ({ proj, statKey }: { proj: PlayerProjectionDetail | undefined; statKey: StatKey }) => {
        const val = proj?.projected_stats?.[statKey] ?? 0;
        const rank = proj?.opp_stat_ranks?.[statKey];
        const rankColor = getRankColor(rank, leagueSize);
        const fp = val * (STAT_POINT_VALUES[statKey] ?? 0);
        return (
            <div className="text-right leading-tight">
                <span className="text-gray-100 text-xs">{fmtProj(fp)}</span>
                {rank != null && (
                    <span className={`text-badge ml-0.5 ${rankColor}`}>(#{rank})</span>
                )}
            </div>
        );
    };

    const isLoading = isPoolLoading || (isLoadingProjections && availablePlayers.length === 0);

    return (
        <>
        <div className="flex flex-col flex-grow min-h-0">
            {/* Search (hidden on desktop when header search is visible) */}
            {!hideSearch && (
                <div className="mb-2">
                    <input
                        type="text"
                        placeholder="Search Players..."
                        value={searchTerm}
                        onChange={e => onSearchTermChange(e.target.value)}
                        className="w-full p-3 rounded-lg bg-gray-900 text-white border-2 border-gray-700 focus:border-accent-yellow focus:outline-none placeholder-gray-500 text-base font-medium"
                    />
                </div>
            )}

            {/* Position filter */}
            <div className="mb-3">
                <HorizontalPillFilter
                    items={filterItems}
                    selectedId={positionFilter}
                    onSelect={id => onPositionFilterChange(id as PositionFilter)}
                />
            </div>

            {/* Legend */}
            <div className="mb-2 px-1 text-xs text-accent-yellow font-heading font-bold">
                Projected Points by Stat (OPRK by Stat)
            </div>

            {/* Table */}
            <div
                className="overflow-y-auto flex-grow border-t border-gray-700"
                ref={scrollRef}
            >
                {isLoading ? (
                    <div className="flex items-center justify-center h-32">
                        <Spinner size="md" color="text-accent-yellow" />
                    </div>
                ) : loadError ? (
                    <div className="p-4 text-center text-secondary text-sm">{loadError}</div>
                ) : (
                    <>
                        {/* Sticky header */}
                        <div className="sticky top-0 z-10 bg-primary/95 backdrop-blur">
                            <div className={`grid items-center px-3 py-2 border-b border-gray-700 text-badge uppercase tracking-wide text-info-blue font-heading font-bold ${GRID}`}>
                                <SortBtn label="Name"  colKey="name"          sortCol={sortCol} sortDir={sortDir} onClick={handleSort} align="left" />
                                <SortBtn label="PROJ"  colKey="projected"     sortCol={sortCol} sortDir={sortDir} onClick={handleSort} />
                                <SortBtn label="GL"    colKey="Goal"          sortCol={sortCol} sortDir={sortDir} onClick={handleSort} />
                                <SortBtn label="SH"    colKey="Shot"          sortCol={sortCol} sortDir={sortDir} onClick={handleSort} />
                                <SortBtn label="SoT"   colKey="ShotOnTarget"  sortCol={sortCol} sortDir={sortDir} onClick={handleSort} />
                                <SortBtn label="SC"    colKey="SCA"           sortCol={sortCol} sortDir={sortDir} onClick={handleSort} />
                                <SortBtn label="GC"    colKey="GCA"           sortCol={sortCol} sortDir={sortDir} onClick={handleSort} />
                                <SortBtn label="IP"    colKey="ProgPass"      sortCol={sortCol} sortDir={sortDir} onClick={handleSort} />
                                <SortBtn label="IC"    colKey="ProgCarry"     sortCol={sortCol} sortDir={sortDir} onClick={handleSort} />
                                <SortBtn label="DIS"   colKey="Dispossessed"  sortCol={sortCol} sortDir={sortDir} onClick={handleSort} />
                                <div className="text-center text-badge">Add</div>
                            </div>
                        </div>

                        {rows.length === 0 ? (
                            <div className="p-4 text-center text-gray-500 text-sm italic">No players match your filters.</div>
                        ) : (
                            <div style={{ height: `${rowVirtualizer.getTotalSize()}px`, position: 'relative' }}>
                                {rowVirtualizer.getVirtualItems().map((virtualRow) => {
                                    const { dfs, proj } = rows[virtualRow.index];
                                    const inLineup      = lineupPlayerIds.has(dfs.id);
                                    const isLocked      = lockedPlayerIds.has(dfs.id);
                                    const isSalaryValid = typeof dfs.dfs_salary === 'number' && dfs.dfs_salary >= 0;
                                    const isAffordable  = remainingSalary === undefined || !isSalaryValid || (dfs.dfs_salary ?? 0) <= remainingSalary;
                                    const notInSquad    = dfs.availability_status === 'NOT_IN_SQUAD';
                                    const isDisabled    = isLocked || inLineup || !isAffordable || !isSalaryValid || notInSquad;

                                    let btnText: ReactNode = '+ Add';
                                    if (isLocked)        btnText = <Lock size={14} />;
                                    else if (inLineup)   btnText = <CircleCheck size={14} />;
                                    else if (notInSquad) btnText = 'N/A';

                                    return (
                                        <div
                                            key={dfs.id}
                                            className="absolute top-0 left-0 w-full"
                                            style={{ height: `${virtualRow.size}px`, transform: `translateY(${virtualRow.start}px)` }}
                                        >
                                            <div
                                                className={`grid items-center px-3 h-full border-b border-gray-800
                                                    ${inLineup ? 'bg-blue-900/20' : 'hover:bg-primary/60 active:bg-primary/60'}
                                                    ${isDisabled && !inLineup ? 'opacity-60' : ''}
                                                    ${GRID}`}
                                            >
                                                {/* Name */}
                                                <button
                                                    type="button"
                                                    className={`leading-tight pr-1 text-left w-full min-w-0 ${!isLocked ? 'group/name' : ''}`}
                                                    onClick={() => !isLocked && handlePlayerRowClick(dfs)}
                                                >
                                                    <div className={`font-medium text-gray-100 text-xs truncate ${!isLocked ? 'group-hover/name:text-accent-yellow group-hover/name:underline group-hover/name:underline-offset-2 group-active/name:text-accent-yellow group-active/name:underline group-active/name:underline-offset-2' : ''}`} title={formatPlayerDisplayName(dfs)}>
                                                        {formatPlayerDisplayName(dfs)}
                                                    </div>
                                                    <div className="flex items-center gap-1">
                                                        <span className="text-badge text-gray-500">{dfs.primary_position || ''}</span>
                                                        <AvailabilityBadge status={dfs.availability_status} isLocked={isLocked} />
                                                        <InjuryBadge status={dfs.injury_status} details={dfs.injury_details} />
                                                    </div>
                                                </button>

                                                {/* PROJ total + aggregate OPRK */}
                                                {(() => {
                                                    const total = calcTotalProjected(proj);
                                                    const oprk = dfs.oprk;
                                                    const oprkColor = getRankColor(oprk ?? undefined, leagueSize);
                                                    return (
                                                        <div className="text-right leading-tight">
                                                            <span className="text-accent-yellow text-xs font-bold">{(total ?? 0).toFixed(1)}</span>
                                                            {oprk != null && (
                                                                <span className={`text-badge ml-0.5 ${oprkColor}`}>(#{oprk})</span>
                                                            )}
                                                        </div>
                                                    );
                                                })()}

                                                <StatCell proj={proj} statKey="Goal" />
                                                <StatCell proj={proj} statKey="Shot" />
                                                <StatCell proj={proj} statKey="ShotOnTarget" />
                                                <StatCell proj={proj} statKey="SCA" />
                                                <StatCell proj={proj} statKey="GCA" />
                                                <StatCell proj={proj} statKey="ProgPass" />
                                                <StatCell proj={proj} statKey="ProgCarry" />
                                                <StatCell proj={proj} statKey="Dispossessed" />

                                                {/* Salary + Add */}
                                                <div className="flex flex-col items-center gap-0.5">
                                                    {isSalaryValid && (
                                                        <div className={`text-badge font-bold font-heading ${!isAffordable ? 'text-secondary' : 'text-accent-yellow'}`}>{fmtSalary(dfs.dfs_salary)}</div>
                                                    )}
                                                    <button
                                                        onClick={e => { e.stopPropagation(); if (!isDisabled) onAddPlayer(dfs); }}
                                                        disabled={isDisabled}
                                                        className={`px-2 py-1 rounded text-xs font-bold transition-colors
                                                            ${isDisabled
                                                                ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                                                                : 'bg-green-600 hover:bg-green-500 active:bg-green-500 text-white'}`}
                                                    >
                                                        {btnText}
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>

        {selectedPlayerForModal && modalCompInfo && (
            <PlayerProfileModal
                isOpen={isProfileModalOpen}
                onClose={() => { setIsProfileModalOpen(false); setSelectedPlayerForModal(null); }}
                player={selectedPlayerForModal}
                competitionId={modalCompInfo.id}
                rankCompetitionIds={rankCompetitionIds}
                competitionName={modalCompInfo.name}
                dfsContestId={contestId}
            />
        )}
        </>
    );
};

export default ProjectionsTab;
